import $ from "jquery";

class AddToCartButton {
  constructor(buttonSelector, options = {}) {
    this.button = $(buttonSelector);
    if (!this.button.length) return;

    this.container = this.button.closest(".product-variant-selector");
    this.quantityInput = this.container.find(".quantity-input");
    this.quantityDecrease = this.container.find(".quantity-decrease");
    this.quantityIncrease = this.container.find(".quantity-increase");
    this.skuElement = this.container.find(".product-sku");

    this.options = {
      apiEndpoint: "/cart/addajax",
      ...options,
    };

    this.bindEvents();
    this.initializeQuantity();
    this.updateButtonState();
  }

  bindEvents() {
    // Xử lý click add to cart
    this.button.on("click", (e) => {
      e.preventDefault();
      this.handleAddToCart();
    });

    // Xử lý quantity input
    this.quantityInput.on("change", () => {
      this.validateQuantity();
    });

    // Xử lý quantity buttons
    this.quantityDecrease.on("click", () => {
      this.decreaseQuantity();
    });

    this.quantityIncrease.on("click", () => {
      this.increaseQuantity();
    });

    // Sử dụng MutationObserver thay vì DOMSubtreeModified
    this.observeSkuChanges();
  }

  observeSkuChanges() {
    // Tạo MutationObserver để theo dõi thay đổi của SKU element
    this.skuObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "data-id"
        ) {
          this.updateButtonState();
        }
      });
    });

    // Bắt đầu observe
    this.skuObserver.observe(this.skuElement[0], {
      attributes: true,
      attributeFilter: ["data-id"],
    });
  }

  getVariantId() {
    // Lấy variant ID từ data-id của SKU element
    return this.skuElement.attr("data-id");
  }

  getUomId() {
    // Lấy UOM ID từ data-uom-id của SKU element
    return this.skuElement.attr("data-uom-id");
  }

  getUomName() {
    // Lấy UOM Name từ data-uom-name của SKU element
    return this.skuElement.attr("data-uom-name");
  }

  getCalculatedPrice() {
    // Lấy variant data từ container
    const variantsData = JSON.parse(
      this.container.find(".product-variants-data").text()
    );
    const variantId = this.getVariantId();
    const uomId = this.getUomId();

    if (!variantId || !variantsData) {
      return 0;
    }

    // Tìm variant hiện tại
    const currentVariant = variantsData.find(
      (variant) => variant.id == variantId
    );
    if (!currentVariant) {
      return 0;
    }

    // Tính toán giá sử dụng VariantHelper
    return window.VariantHelper.calculateSellPriceForUom(currentVariant, uomId);
  }

  getSelectedOptions() {
    const selectedOptions = [];

    // Lấy tất cả option items đang active trong container
    this.container.find(".product-option").each((index, el) => {
      const optionGroup = $(el);
      const optionId = optionGroup.data("option-id");
      const activeOption = optionGroup.find(".option-item.option-item-active");

      if (activeOption.length) {
        const optionValueId = activeOption.data("option-value-id");
        const optionValueName = activeOption.text().trim();

        selectedOptions.push({
          option_id: optionId,
          option_value_id: optionValueId,
          option_value_name: optionValueName,
        });
      }
    });

    return selectedOptions;
  }

  hasValidVariant() {
    const variantId = this.getVariantId();
    return variantId && variantId !== "";
  }

  updateButtonState() {
    if (this.hasValidVariant()) {
      this.enable();
    } else {
      this.disable();
    }
  }

  initializeQuantity() {
    // Set default quantity
    if (!this.quantityInput.val()) {
      this.quantityInput.val(1);
    }
    this.validateQuantity();
  }

  getQuantity() {
    return parseInt(this.quantityInput.val()) || 1;
  }

  setQuantity(quantity) {
    this.quantityInput.val(quantity);
    this.validateQuantity();
  }

  decreaseQuantity() {
    const currentQty = this.getQuantity();
    if (currentQty > 1) {
      this.setQuantity(currentQty - 1);
    }
  }

  increaseQuantity() {
    const currentQty = this.getQuantity();
    const maxQty = parseInt(this.quantityInput.attr("max")) || 99;
    if (currentQty < maxQty) {
      this.setQuantity(currentQty + 1);
    }
  }

  validateQuantity() {
    const quantity = this.getQuantity();
    const min = parseInt(this.quantityInput.attr("min")) || 1;
    const max = parseInt(this.quantityInput.attr("max")) || 99;

    if (quantity < min) {
      this.setQuantity(min);
    } else if (quantity > max) {
      this.setQuantity(max);
    }
  }

  enable() {
    const text = this.button.data("text-enabled") || "Add to Cart";
    this.button.prop("disabled", false).removeClass("disabled").text(text);
  }

  disable() {
    const text = this.button.data("text-disabled") || "Please select a variant";
    this.button.prop("disabled", true).addClass("disabled").text(text);
  }

  showLoading() {
    this.button.prop("disabled", true).text("Đang thêm vào giỏ hàng...");
  }

  showSuccess() {
    const originalText = this.button.data("text-enabled") || "Add to Cart";
    this.button.text("Đã thêm!").addClass("success");

    setTimeout(() => {
      this.button.removeClass("success").text(originalText);
      this.enable();
    }, 2000);
  }

  showError(message) {
    const originalText = this.button.data("text-enabled") || "Add to Cart";
    this.button.text("Error!").addClass("error");

    // Có thể thêm toast notification ở đây
    console.error("Add to cart error:", message);

    setTimeout(() => {
      this.button.removeClass("error").text(originalText);
      this.enable();
    }, 2000);
  }

  async handleAddToCart() {
    const variantId = this.getVariantId();

    if (!variantId) {
      console.warn("No variant selected");
      return;
    }

    const quantity = this.getQuantity();
    const uomId = this.getUomId();
    const uomName = this.getUomName();

    try {
      this.showLoading();

      // API call với variant ID và quantity
      const response = await this.addToCartAPI(variantId, quantity);

      if (response.success) {
        this.showSuccess();

        // Emit event để các module khác có thể lắng nghe (ví dụ: update cart count)
        this.container.trigger("cart:added", {
          product_variant_id: variantId,
          uom_quantity: quantity,
          uom_id: uomId,
          uom_name: uomName,
          response: response,
        });

        // Emit global event for cart module
        document.dispatchEvent(
          new CustomEvent("cart:updated", {
            detail: {
              cart: response.cart || { quantity: 1 },
              action: "added",
              variantId: variantId,
              quantity: quantity,
            },
          })
        );
      } else {
        throw new Error(response.error?.join(". ") || "Failed to add to cart");
      }
    } catch (error) {
      this.showError(error.message);
    }
  }

  async addToCartAPI(variantId, quantity) {
    const section = this.container.data("cart-section") || "productdetail";
    const uomId = this.getUomId();
    const uomName = this.getUomName();
    const calculatedPrice = this.getCalculatedPrice();
    const selectedOptions = this.getSelectedOptions();

    const response = await fetch(this.options.apiEndpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify({
        section: section,
        product_variant_id: parseInt(variantId),
        uom_quantity: parseInt(quantity),
        uom_id: uomId ? parseInt(uomId) : null,
        uom_name: uomName,
        calculated_price: calculatedPrice,
        selected_options: selectedOptions,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }
}

export default AddToCartButton;
